import React, { Component } from 'react'
import { Redirect, Route, Switch } from 'react-router-dom'
import { authenticate, logout } from '../actions/security'
import LoginForm from './LoginForm'
import ForgotPasswordForm from './ForgotPasswordForm'
import ResetPasswordForm from './ResetPasswordForm'
import SignUp from './SignUpForm'
import { getLoginPageElement } from '../api/login'
import { withStyles } from '@material-ui/core'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { setBrowserTabTitle } from '../../../helpers/setBrowserTabTitle'
import { theme } from 'constants/theme'
import { encodedSVG } from './loginSvg'
import OtpPage from './OtpPage'
import { HOME_PATH } from '../../../configs'
import { probeAdminSession, resumeAuth0Session } from '../helpers/resumeAuth0Session'
import { getQcsHandoffId } from '../helpers/getQcsHandoffId'
import { markQcsHandoffSession } from '../helpers/qcsHandoffSession'
import { HANDOFF_RESULT, isQcsHandoffDone, loginWithQcsHandoff } from '../helpers/qcsHandoffLogin'
import { QCS_HANDOFF_ERROR_PATH } from '../helpers/qcsHandoffError'
import QcsHandoffLoadingPage from './QcsHandoffLoadingPage'

const POST_LOGIN_PATH = `${HOME_PATH}/`

/**
 * Normalise a post-login redirect. Prefer PrivateRoute's `state.from` (deep link
 * after reload); never bounce back to /login or /otp.
 */
const resolvePostLoginTarget = from => {
  if (!from) return POST_LOGIN_PATH
  if (typeof from === 'string') {
    if (from === '/login' || from.startsWith('/login?') || from === '/otp' || from.startsWith('/otp?')) {
      return POST_LOGIN_PATH
    }
    return from
  }
  if (from.pathname) {
    if (from.pathname === '/login' || from.pathname === '/otp') {
      return POST_LOGIN_PATH
    }
    return {
      pathname: from.pathname,
      search: from.search || '',
      hash: from.hash || '',
      ...(from.state ? { state: from.state } : null),
    }
  }
  return POST_LOGIN_PATH
}

const formStyles = theme => ({
  main: {
    flex: '0 0 50%',
    maxWidth: '50%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-evenly',
    alignItems: 'center',
    padding: '40px',
    position: 'relative',
  },
  '@media (min-width: 320px) and (max-width: 480px)': {
    main: {
      flex: '0 0 100%',
      maxWidth: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-evenly',
      alignItems: 'center',
      padding: '15px',
      position: 'relative',
    },
  },
  formTitle: {
    fontSize: '18px',
    fontWeight: '500',
    margin: 0,
    color: theme.tabs.login.formTitle()
  },
  demoForm: {
    width: '60%',
    margin: '0 0 32px',
    textAlign: 'center',
  },
  demoDiv: {
    margin: '32px 0 8px',
    textAlign: 'left',
  },
  bottomText: {
    position: 'absolute',
    bottom: '60px',
  },
  forgotPasswordLink: {
    fontSize: '13px',
    color: theme.tabs.login.forgotPassword.link,
    display: 'block',
    textAlign: 'center',
    marginTop: '30px',
  },
  forgotPasswordNoteText: {
    fontSize: '14px',
    color: theme.tabs.login.forgotPassword.noteText(),
    marginTop: '-10px',
    marginBottom: '25px',
    fontWeight: '300',
  },
})

class Login extends Component {
  constructor(props) {
    super(props)
    const handoffId = getQcsHandoffId(props.location)
    this.state = {
      from: null,
      title: null,
      subTitle: null,
      imagePath: null,
      isSignUpEnable: false,
      redirectToReferrer: false,
      handoffPending: Boolean(handoffId && !isQcsHandoffDone(handoffId)),
      handoffError: '',
    }
  }

  componentDidMount() {
    this.mounted = true
    const authenticated = JSON.parse(localStorage.getItem('authenticated'))
    if (!this.state.handoffPending) setBrowserTabTitle('Chatbot Platform')
    if (this.state.handoffPending) {
      this.startQcsHandoff(authenticated)
    } else if (authenticated) {
      // Restore the deep link PrivateRoute saved (e.g. /admin/bot/:id/dashboard).
      const savedFrom = this.props.location && this.props.location.state && this.props.location.state.from
      this.authenticateWithReferrer(savedFrom || POST_LOGIN_PATH)
      return
    } else {
      // Auth0 may return here (or PrivateRoute resume failed → /login) with a
      // session cookie but without SPA localStorage — probe then authenticate().
      this.tryResumeAuth0Session()
    }
    getLoginPageElement(element => {
      if (!this.mounted) return
      this.setState({
        title: element?.title,
        subTitle: element?.subTitle,
        imagePath: element?.imagePath,
        isSignUpEnable: element?.isSignUpEnable,
      })
    })
  }

  componentDidUpdate(prevProps) {
    const handoffId = getQcsHandoffId(this.props.location)
    if (!handoffId || handoffId === getQcsHandoffId(prevProps.location)) return
    if (this.state.handoffPending || isQcsHandoffDone(handoffId)) return
    if (JSON.parse(localStorage.getItem('authenticated'))) return
    this.setState({ handoffPending: true, handoffError: '' }, this.runQcsHandoff)
  }

  componentWillUnmount() {
    this.mounted = false
  }

  startQcsHandoff = authenticated => {
    if (!authenticated) {
      this.runQcsHandoff()
      return
    }
    // The localStorage flag can outlive the backend session: check it silently.
    probeAdminSession().then(ok => {
      if (!this.mounted) return
      if (ok) {
        const savedFrom = this.props.location && this.props.location.state && this.props.location.state.from
        this.authenticateWithReferrer(savedFrom || POST_LOGIN_PATH)
        return
      }
      logout()
      this.runQcsHandoff()
    })
  }

  runQcsHandoff = () => {
    const handoffId = getQcsHandoffId(this.props.location)
    loginWithQcsHandoff(handoffId).then(result => {
      if (!this.mounted) return
      if (result.type === HANDOFF_RESULT.SUCCESS) {
        this.authenticateWithReferrer({ pathname: `${HOME_PATH}/qcs`, state: { fromHandoffLogin: true } })
        return
      }
      if (result.type === HANDOFF_RESULT.ERROR_PAGE) {
        // replace, not push: Back must not land on /login?type=qcs&id= and re-post the handoff.
        this.props.history.replace({
          pathname: QCS_HANDOFF_ERROR_PATH,
          search: `?reason=${result.reason}`,
        })
        return
      }
      setBrowserTabTitle('Chatbot Platform')
      this.setState({ handoffPending: false, handoffError: result.message })
    })
  }

  tryResumeAuth0Session = () => {
    // Skip re-probe when Auth0SessionResume already failed and sent us here.
    if (this.props.location?.state?.auth0ResumeFailed) return

    resumeAuth0Session().then(ok => {
      if (!ok) return
      const savedFrom = this.props.location && this.props.location.state && this.props.location.state.from
      this.authenticateWithReferrer(savedFrom || POST_LOGIN_PATH)
    })
  }

  authenticateWithReferrer = from => {
    if (getQcsHandoffId(this.props.location)) markQcsHandoffSession()
    authenticate()
    this.setState({
      redirectToReferrer: true,
      from: resolvePostLoginTarget(from),
    })
  }

  render() {
    const { classes } = this.props
    const {
      redirectToReferrer,
      from,
      title,
      subTitle,
      imagePath,
      isSignUpEnable,
      handoffPending,
      handoffError,
    } = this.state

    if (redirectToReferrer) {
      return <Redirect to={from || POST_LOGIN_PATH} />
    }

    if (handoffPending) {
      return <QcsHandoffLoadingPage />
    }

    return (
      <div className="main container-fluid">
        {title && subTitle && imagePath && (
          <div className="row row--main">
            <section
              style={{
                background: `url("${encodedSVG}") top right no-repeat`,
                backgroundColor: theme.tabs.login.imageBackground(),
                backgroundSize: 'contain !important'
              }}
              className="title-section col-sm-6">
              <article className="section__inner">
                <h1 className="title--main">{title}</h1>
                <h4 className="title--small">{subTitle}</h4>
              </article>
            </section>
            <Switch>
              <Route
                path={'/sign-up/:code'}
                component={props => (
                  <SignUp {...props} invited={true} styles={classes} />
                )}
              />
              <PrivateRoute
                isSignUpEnable={isSignUpEnable}
                exact
                path="/sign-up"
                component={() => <SignUp styles={classes} />}
              />
              <Route
                path={'/login'}
                render={routeProps => (
                  <LoginForm
                    {...routeProps}
                    authenticateWithReferrer={this.authenticateWithReferrer}
                    isSignUpEnable={isSignUpEnable}
                    initialErrorMessage={handoffError}
                    styles={classes}
                  />
                )}
              />
              <Route
                path="/forgot-password"
                component={props => <ForgotPasswordForm styles={classes} />}
              />
              <Route
                path="/reset-password"
                component={props => <ResetPasswordForm styles={classes} />}
              />
              <Route
                path="/otp"
                component={props => <OtpPage {...props} styles={classes} authenticateWithReferrer={this.authenticateWithReferrer}/>}
              />
            </Switch>
          </div>
        )}
      </div>
    )
  }
}

const PrivateRoute = ({ component: Component, isSignUpEnable, allowOtp, ...rest }) => (
  <Route
    {...rest}
    render={props =>
      (allowOtp || isSignUpEnable ? (
        <Component {...props} />
      ) : (
        <Redirect
          to={{
            pathname: '/login',
            state: { from: props.location },
          }}
        />
      ))
    }
  />
)
Login.propTypes = {
  classes: PropTypes.object.isRequired,
}

const mapStateToProps = state => ({
  security: state.security,
})

export default withStyles(formStyles, { withTheme: true })(connect(mapStateToProps)(Login))
